import React, { useState } from 'react';
import { Box, Container, Link, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from '@mui/material';
import { Shield, Description, Close } from '@mui/icons-material';
import { useThemeMode } from '../../../../../contexts/ThemeProvider';
import TermsandConditionsContent from '../../../TermsandConditionsContent';
import PrivacyPolicyContent from '../../../PrivacyPolicyContent';

const Footer = ({ config }: any) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);

  const footerConfig = config?.login?.footer || {};
  const primaryColor = config?.theme?.primary || '#002465';
  const contentConfig = config?.login?.footer?.content ;

  const handleLinkClick = (link: any) => {
    if (link.title === 'Privacy Policy') {
      setModalContent('privacy');
      setOpenModal(true);
    } else if (link.title === 'Terms & Conditions') {
      setModalContent('terms');
      setOpenModal(true);
    } else {
      window.open(link.url, '_blank');
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalContent(null);
  };

  const getModalTitle = () => {
    switch (modalContent) {
      case 'privacy':
        return 'Privacy Policy';
      case 'terms':
        return 'Terms & Conditions';
      default:
        return '';
    }
  };

  const renderModalContent = () => {
    switch (modalContent) {
      case 'privacy':
        return <PrivacyPolicyContent  contentConfig ={contentConfig} />;
      case 'terms':
        return <TermsandConditionsContent contentConfig ={contentConfig} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Box
        component="footer"
        sx={{
          backgroundColor: isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(229, 231, 235, 0.5)',
          py: 2,
          px: 3,
          position: 'relative',
          zIndex: 1
        }}
      >
        <Container maxWidth="sm">
          <Box display="flex" justifyContent="center" alignItems="center" gap={4} flexWrap="wrap">
            {(
              footerConfig.links || [
                { title: 'Privacy Policy', url: '#', icon: 'Shield' },
                { title: 'Terms & Conditions', url: '#', icon: 'Description' }
              ]
            ).map((link: any, index: any) => (
              <React.Fragment key={index}>
                <Link
                  component="button"
                  onClick={() => handleLinkClick(link)}
                  sx={{
                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#6b7280',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    '&:hover': {
                      color: isDark ? '#ffffff' : primaryColor,
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {link.icon === 'Shield' && (
                    <Shield
                      sx={{
                        fontSize: 16,
                        color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#9ca3af'
                      }}
                    />
                  )}
                  {link.icon === 'Description' && (
                    <Description
                      sx={{
                        fontSize: 16,
                        color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#9ca3af'
                      }}
                    />
                  )}
                  {link.title}
                </Link>
                {index < (footerConfig.links || []).length - 1 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderColor: isDark ? 'rgba(255, 255, 255, 0.2)' : '#d1d5db',
                      display: { xs: 'none', sm: 'block' }
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Container>
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
            color: isDark ? '#ffffff' : '#000000',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            borderBottom: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            fontSize: '1.25rem',
            fontWeight: 600
          }}
        >
          {getModalTitle()}
          <IconButton
            onClick={handleCloseModal}
            size="small"
            sx={{
              color: isDark ? '#ffffff' : '#000000',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 3,
            '& .font-extrabold': {
              fontWeight: 800
            },
            '& .font-bold': {
              fontWeight: 700
            },
            '& .text-sm': {
              fontSize: '0.875rem',
              lineHeight: '1.25rem'
            },
            '& .mb-2': {
              marginBottom: '0.5rem'
            },
            '& .mt-4': {
              marginTop: '1rem'
            },
            color: isDark ? '#ffffff' : '#000000',
            '& div': {
              color: 'inherit'
            }
          }}
        >
          <Box
            sx={{
              color: isDark ? '#ffffff' : '#000000',
              '& *': { color: 'inherit' }
            }}
          >
            {renderModalContent()}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Footer;
